import axios from 'axios';
import { AccountHierarchy } from 'types/api/AccountHierarchy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function getCenters(accountId: number) {
  return axios.get(`/api/v2/accounts/${accountId}/nodes/centers`);
}

export function getClassrooms(accountId: number, nodeId: number) {
  return axios.get(`/api/v2/accounts/${accountId}/nodes/${nodeId}/classrooms`);
}

export function replaceNodeWithAccount(
  accountId: number,
  nodeId: number,
  externalAccountId: number
) {
  return axios.put(
    `/api/v2/accounts/${accountId}/nodes/${nodeId}/replace_by_account`,
    {
      external_account_id: externalAccountId
    }
  );
}

export function useReplaceNodeWithAccount(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      nodeId,
      externalAccountId
    }: {
      accountId: number;
      nodeId: number;
      externalAccountId: number;
    }) => replaceNodeWithAccount(accountId, nodeId, externalAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      if (onSuccess) onSuccess();
    }
  });
}

export function getCanPromoteNodeToAccount(
  accountId: number,
  nodeId: number,
  externalAccountId: number
) {
  return axios.get(
    `/api/v2/accounts/${accountId}/nodes/${nodeId}/can_replace_by_account`,
    {
      params: { external_account_id: externalAccountId }
    }
  );
}

export function useGetCanPromoteNodeToAccount(
  accountId: number,
  nodeId: number,
  externalAccountId: number
) {
  return useQuery({
    queryKey: [
      'nodes',
      accountId,
      nodeId,
      'can_replace_by_account',
      externalAccountId
    ],
    queryFn: () =>
      getCanPromoteNodeToAccount(accountId, nodeId, externalAccountId),
    enabled: !!accountId && !!nodeId && !!externalAccountId
  });
}

export function promoteNodeToAccount(
  accountId: number,
  nodeId: number,
  sfAccountId: string
) {
  return axios.put(
    `/api/v2/accounts/${accountId}/nodes/${nodeId}/promote_to_account`,
    {
      sf_account_id: sfAccountId
    }
  );
}

export function usePromoteNodeToAccount(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      nodeId,
      sfAccountId
    }: {
      accountId: number;
      nodeId: number;
      sfAccountId: string;
    }) => promoteNodeToAccount(accountId, nodeId, sfAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      if (onSuccess) onSuccess();
    }
  });
}

export function updateNode(nodeId: number, node: AccountHierarchy) {
  return axios.put(`/api/v2/nodes/${nodeId}`, {
    node
  });
}

export function addNode(payload = {}) {
  return axios.post('/api/v2/nodes', payload);
}

export function getNode(nodeId: number) {
  return axios.get(`/api/v2/nodes/${nodeId}`);
}

export function useGetNode(nodeId: number) {
  return useQuery({
    queryKey: ['nodes', nodeId],
    queryFn: () => getNode(nodeId),
    enabled: !!nodeId
  });
}

export function deleteNode(nodeId: number) {
  return axios.delete(`/api/v2/nodes/${nodeId}`);
}

export function addTaggingToNode(nodeId: number, tag_id: number) {
  return axios.post(`/api/v2/nodes/${nodeId}/taggings`, {
    tagging: { tag_id }
  });
}

export function deleteNodeTag(nodeId: number, tagId: number, endDate: string) {
  return axios.delete(`/api/v2/nodes/${nodeId}/taggings/${tagId}`, {
    data: { end_date: endDate }
  });
}

export function updateGrade(nodeId: number, params = {}) {
  return axios.put(`/api/v2/nodes/${nodeId}/grades`, { node: params });
}

type NodeUser = {
  name: string;
  email: string;
  role: string;
};

type GetNodeUsersRes = {
  users: NodeUser[];
  pagination: {
    total_pages: number;
    current_page: number;
  };
};

export function getNodeUsers(nodeId: number) {
  return axios.get<GetNodeUsersRes>(`/api/v2/nodes/${nodeId}/users`);
}

export function getNodeEligibleParents(nodeId: number) {
  return axios.get(`/api/v2/nodes/${nodeId}/eligible_parents`);
}
